/**
 * Audit-events read hook (ADR-003 + ADR-005).
 *
 * Subtask 06 cutover: replaces the empty-array stub with a real React-Query
 * call that dispatches by `scope`:
 *
 *  - `'global'`                                       → `GET /v1/audit/events`
 *  - `{ object_type: 'document', object_id }`         → `GET /v1/documents/{id}/audit`
 *  - `{ object_type: 'twin_entity', entity_type, entity_id }`
 *                                                     → `GET /v1/twin/entities/{type}/{id}/audit`
 *
 * The two specialised endpoints pre-fill `object_type`/`object_id` server-side
 * (per ADR-003) so the hook does not pass them as query params. Lens-tree
 * leaf selection (`category` / `subcategory`) is forwarded to the *global*
 * endpoint only — Backend translates it to `event_type IN (...)` via
 * `event_type_mapper.category_to_event_types`. Forwarding category to a
 * scoped endpoint would be redundant (object filter already constrains the
 * feed) and the per-doc / per-entity routes don't take those params.
 *
 * The hook also bridges the wire shape (`AuditEvent` per ADR-003) into the
 * design-system display shape (`AuditLogEntry`):
 *
 *  - `event_id`      → `id`
 *  - `occurred_at`   → `timestamp`
 *  - `actor_label`   → `user.name` (falls back to `actor_id` with `unresolved=true`)
 *  - `object_label`  → `entity.name` (falls back to `object_id` with `unresolved=true`)
 *  - `metadata.explanation` (or event_type humanised) → `description`
 *  - server `event_type` prefix → German `category` label (display only)
 *
 * Cursor pagination (next_cursor) is intentionally not surfaced yet; the
 * first page (limit 200) is enough for the unified feed launch. Wiring
 * `useInfiniteQuery` is a follow-up that can land without a contract change
 * once the design-system table grows an "infinite" mode.
 */
import { useQuery } from '@tanstack/react-query';
import type { AuditLogEntry } from '@hofsync/design-system';
import api from '../services/api';
import {
  mapServerEventToEntry,
  type ServerAuditEventsResponse,
} from './auditEventMappers';

export type AuditEventsScope =
  | 'global'
  | { object_type: 'document'; object_id: string }
  | { object_type: 'twin_entity'; entity_type: string; entity_id: string };

export interface UseAuditEventsParams {
  scope: AuditEventsScope;
  category?: string;
  subcategory?: string;
  since?: string;
  until?: string;
}

export interface UseAuditEventsResult {
  entries: AuditLogEntry[];
  isLoading: boolean;
  isError: boolean;
  error?: Error;
  refetch: () => void;
}

const PAGE_LIMIT = 200;

function buildUrl(scope: AuditEventsScope): string {
  if (scope === 'global') return '/v1/audit/events';
  if (scope.object_type === 'document') {
    return `/v1/documents/${scope.object_id}/audit`;
  }
  return `/v1/twin/entities/${scope.entity_type}/${scope.entity_id}/audit`;
}

function buildParams(params: UseAuditEventsParams): Record<string, string | number> {
  const out: Record<string, string | number> = { limit: PAGE_LIMIT };
  if (params.since) out.since = params.since;
  if (params.until) out.until = params.until;
  // Lens-tree filters apply only to the global feed (per ADR-003 the scoped
  // endpoints don't accept them — Backend would 422). Forwarding them here
  // would be a silent client-side bug.
  if (params.scope === 'global') {
    if (params.category) out.category = params.category;
    if (params.subcategory) out.subcategory = params.subcategory;
  }
  return out;
}

function scopeKey(scope: AuditEventsScope): readonly unknown[] {
  if (scope === 'global') return ['global'] as const;
  if (scope.object_type === 'document') {
    return ['document', scope.object_id] as const;
  }
  return ['twin_entity', scope.entity_type, scope.entity_id] as const;
}

// `farm-data` leaves all map to the same `twin.entity.*` event_types on
// the backend (one mapping per entity family). The server has no
// per-leaf entity_type filter yet, so the page returns a mixed
// twin-entity feed for every leaf. We narrow client-side by parsing the
// `object_id` encoding (`{entity_type}/{entity_id}`, ADR-003) and
// keeping only rows whose entity_type matches the leaf id (singular,
// the worker emits `object_id` with the same id family as the lens leaf
// — the leaf ids in `historyCategoriesDE` are the entity_type plural,
// e.g. "animals" vs the worker's "animal", so we strip the trailing 's'
// to map). Follow-up: lift this once Backend grows an `entity_type`
// query param on `/audit/events` (ADR-003 amendment).
const FARM_DATA_LEAVES: ReadonlySet<string> = new Set([
  'animals',
  'fields',
  'equipment',
  'buildings',
  'staff',
  'inventory',
]);

function farmDataLeafToEntityType(leaf: string): string | null {
  if (!FARM_DATA_LEAVES.has(leaf)) return null;
  // English plurals → singular entity_type. `equipment`, `staff`,
  // `inventory` are already singular forms in the worker emit path.
  if (leaf === 'animals') return 'animal';
  if (leaf === 'fields') return 'field';
  if (leaf === 'equipment') return 'equipment';
  if (leaf === 'buildings') return 'building';
  if (leaf === 'staff') return 'staff';
  if (leaf === 'inventory') return 'inventory';
  return null;
}

function shouldPostFilterByEntityType(params: UseAuditEventsParams): string | null {
  if (params.scope !== 'global') return null;
  if (params.category !== 'farm-data') return null;
  if (!params.subcategory) return null;
  return farmDataLeafToEntityType(params.subcategory);
}

function entityTypeFromObjectId(objectId: string | null | undefined): string | null {
  if (!objectId) return null;
  const slashIdx = objectId.indexOf('/');
  if (slashIdx <= 0) return null;
  return objectId.slice(0, slashIdx);
}

export function useAuditEvents(params: UseAuditEventsParams): UseAuditEventsResult {
  const url = buildUrl(params.scope);
  const queryParams = buildParams(params);

  const query = useQuery<ServerAuditEventsResponse>({
    queryKey: [
      'audit-events',
      ...scopeKey(params.scope),
      params.category ?? null,
      params.subcategory ?? null,
      params.since ?? null,
      params.until ?? null,
    ],
    queryFn: async () => {
      const res = await api.get<ServerAuditEventsResponse>(url, { params: queryParams });
      return res.data;
    },
  });

  const entityTypeFilter = shouldPostFilterByEntityType(params);
  const rawEvents = query.data?.events ?? [];
  const filteredEvents = entityTypeFilter
    ? rawEvents.filter(e => entityTypeFromObjectId(e.object_id) === entityTypeFilter)
    : rawEvents;
  const entries = filteredEvents.map(mapServerEventToEntry);

  return {
    entries,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : undefined,
    refetch: () => {
      void query.refetch();
    },
  };
}
